/**
 * Helper functions for building grid rows from axis values.
 *
 * Handles the nested loop logic of creating cells for each x/y coordinate.
 */
import {type RunWithHistoryAndMediaWandb} from '../../../MediaCard';
import {type StepMetaRun} from '../../types';
import {resolveCurrentStep} from '../../utils/stepUtils';
import {type GridCell, type GridRow, type TransformInput} from '../types';
import {createCellsForMediaKeys, createEmptyCell} from './cells';

const DEBUG = false;
const log = DEBUG ? console.log : () => {};

/**
 * Determines which dimension (step or index) is controlled by a slider.
 *
 * @param xAxis - X-axis type
 * @param yAxis - Y-axis type
 * @returns The controlled dimension
 */
export function getControlledDimension(
  xAxis: string,
  yAxis: string
): 'step' | 'index' {
  // If neither axis is step, then step is controlled
  if (xAxis !== 'step' && yAxis !== 'step') {
    return 'step';
  }
  // If neither axis is index, then index is controlled
  if (xAxis !== 'index' && yAxis !== 'index') {
    return 'index';
  }
  // Default to index
  return 'index';
}

/**
 * Resolves cell coordinates (run, step, index) from x/y axis values.
 *
 * @param xAxis - X-axis type
 * @param xValue - X-axis value
 * @param yAxis - Y-axis type
 * @param yValue - Y-axis value
 * @param controlledDimension - Which dimension is controlled by slider
 * @param currentStep - Current step from slider
 * @param currentIndex - Current index from slider
 * @param runs - Array of runs
 * @returns Cell coordinates or null if unable to resolve
 */
export function resolveCellCoordinates(
  xAxis: string,
  xValue: number | RunWithHistoryAndMediaWandb,
  yAxis: string,
  yValue: number | RunWithHistoryAndMediaWandb,
  controlledDimension: 'step' | 'index',
  currentStep: number,
  currentIndex: number,
  runs: TransformInput['runs']
): {run: RunWithHistoryAndMediaWandb; step: number; mediaIndex: number} | null {
  // unless the axis is a run, the values should be numbers
  if (
    (xAxis !== 'run' && typeof xValue !== 'number') ||
    (yAxis !== 'run' && typeof yValue !== 'number')
  ) {
    return null;
  }
  let run: any = null;
  let step: number = currentStep;
  let mediaIndex: number = currentIndex;

  if (xAxis === 'run') {
    run = typeof xValue === 'number' ? runs[xValue] : xValue;
  } else if (yAxis === 'run') {
    run = typeof yValue === 'number' ? runs[yValue] : yValue;
  } else {
    run = runs[0]; // First run by default
  }

  if (!run) {
    return null;
  }

  if (xAxis === 'step') {
    step = xValue as number;
  } else if (yAxis === 'step') {
    step = yValue as number;
  } else if (controlledDimension === 'step') {
    step = currentStep;
  }

  if (xAxis === 'index') {
    mediaIndex = xValue as number;
  } else if (yAxis === 'index') {
    mediaIndex = yValue as number;
  } else if (controlledDimension === 'index') {
    mediaIndex = currentIndex;
  }

  return {run, step, mediaIndex};
}

/**
 * Builds grid rows from axis values using nested loops.
 *
 * @param xAxis - X-axis type
 * @param yAxis - Y-axis type
 * @param xAxisValues - X-axis values (columns)
 * @param yAxisValues - Y-axis values (rows)
 * @param controlledDimension - Which dimension is controlled by slider
 * @param input - Transform input data
 * @param snapToExistingStep - Whether to snap steps to existing values
 * @param effectiveColumnCount - Optional column count limit (for empty cell padding)
 * @returns Array of grid rows
 */
export function buildGridRows(
  xAxis: string,
  yAxis: string,
  xAxisValues: any[],
  yAxisValues: any[],
  controlledDimension: 'step' | 'index',
  input: TransformInput,
  effectiveColumnCount?: number
): GridRow[] {
  const {runs, stepMeta, config, allMasks} = input;
  const {mediaKeys} = config;

  if (!mediaKeys || mediaKeys.length === 0) {
    return [];
  }

  const rows: GridRow[] = [];

  for (let y = 0; y < yAxisValues.length; y++) {
    const yValue = yAxisValues[y];
    const cells: GridCell[] = [];

    // Loop up to effectiveColumnCount (or all xAxisValues if no limit)
    // Note: effectiveColumnCount is already divided by mediaKeys.length to account
    // for multiple cells per x-axis value
    const colCount = effectiveColumnCount ?? xAxisValues.length;

    for (let x = 0; x < colCount; x++) {
      // If we've run out of axis data, add empty cells for remaining columns
      if (x >= xAxisValues.length) {
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
        continue;
      }

      const xValue = xAxisValues[x];

      const currentStep = resolveCurrentStep(
        config.stepIndex,
        stepMeta,
        config.mediaSliderKey
      );
      const currentIndex = config.mediaIndex ?? 0;
      const cellCoords = resolveCellCoordinates(
        xAxis,
        xValue,
        yAxis,
        yValue,
        controlledDimension,
        currentStep,
        currentIndex,
        runs
      );

      if (!cellCoords) {
        // Add empty cells for each media key when coordinates can't be resolved
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
        continue;
      }

      const run = cellCoords.run;
      const runStepMeta: StepMetaRun | undefined = stepMeta.runs.find(
        r => r.id === run.name
      );

      if (!runStepMeta) {
        // Run not found in runs array - add empty cells and continue
        console.warn(
          `[gridRowHelpers] Run not found in runs array: ${(cellCoords.run as any)?.name ?? 'unknown'}`,
          run.name,
          runStepMeta
        );
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
        continue;
      }

      // Create cells for ALL media keys
      // This expands each x-axis value into multiple columns, one per media key
      // Use the run from the runs array to ensure we have the correct reference
      // Pass runStepMeta so createCell can determine the correct slider key for wall time
      const mediaCells = createCellsForMediaKeys(
        run,
        runStepMeta,
        cellCoords.step,
        cellCoords.mediaIndex,
        mediaKeys,
        config,
        allMasks,
        input.mediaType
      );

      if (mediaCells.length > 0) {
        cells.push(...mediaCells);
      } else {
        // If no cells created, add empty cells for each media key to maintain structure
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
      }
    }

    if (cells.length > 0) {
      rows.push({
        cells,
        rowKey: `${yAxis}-${y}-${yValue}`,
      });
    }
  }

  log(
    `[gridRowHelpers] Built ${rows.length} rows with ${rows[0]?.cells.length ?? 0} columns each`
  );

  return rows;
}
